import seedrandom from 'seedrandom';
import { ProblemResponse } from '../api/problems';

// sort problems according to "previous" attribute
// this assumes that exactly one problem has previous set to null
// i.e. the first problem
export const sortProblemSet = (problems: ProblemResponse[]): ProblemResponse[] => {
  // return unaltered array if empty
  if (problems.length < 1) {
    return problems;
  }

  // map of problem id to problem
  const problemMap = new Map<number, ProblemResponse>();
  // map of problem id to previous problem id
  const nextProblemMap = new Map<number, number>();
  // problem iterator
  let problemIter: ProblemResponse | undefined;

  // loop over problems and fill in problem and next problem maps
  problems.forEach(p => {
    problemMap.set(p.id, p);
    if (p.previous !== null) {
      nextProblemMap.set(p.previous, p.id);
    } else {
      problemIter = p;
    }
  });

  // throw error if first problem not found
  if (problemIter === undefined) {
    throw new Error('First problem not found');
  }

  const sortedProblems: ProblemResponse[] = [];
  // sort problems by insertion sort using next problem map
  while (true) {
    sortedProblems.push(problemIter);
    let nextProblem = nextProblemMap.get(problemIter.id)
    // stop when there is no next problem
    if (nextProblem === undefined) {
      break;
    }

    problemIter = problemMap.get(nextProblem);
    // stop when unable to find next problem
    if (problemIter === undefined) {
      break;
    }
  }

  return sortedProblems;
};

// return random problem as problem of the day
// current date is used as random seed for consistency throughout the day
export const getProblemOfTheDay = (problems: ProblemResponse[]): ProblemResponse => {
  // get today's date
  const today = new Date();
  // get seed from today's date that is consistent throughout the day
  const seed = today.toISOString().substring(0, 10);
  // initialize random number generator
  const rng = seedrandom(seed);
  // get random array index
  const randomProblemIndex = Math.floor(rng() * (problems.length));
  // get random problem from index
  const randomProblem = problems[randomProblemIndex];

  return randomProblem;
};

// return n random problems from a given list of problems
export const getNRandomProblems = (problems: ProblemResponse[], n: number): ProblemResponse[] => {
  // if n is less than number of problems, return all problems
  if (n >= problems.length) {
    return problems;
  }

  // create new array
  const shuffledProblems = [...problems];
  // shuffle array
  for (let i = problems.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledProblems[i], shuffledProblems[j]] = [shuffledProblems[j], shuffledProblems[i]];
  }

  // return first n problems
  return shuffledProblems.slice(0, n);
};
